import React, { useState, useRef, useCallback, useEffect } from "react";
import {
    createFeatureApi,
    markWorkProductsNeedingRebuild,
    startQuickCompare,
    getCompareStatus,
    applySpecificationChanges
} from "../../../api/api";
import { createUserStory, updateUserStory, getUserStory } from "../../../api/userStoryApi";
import { useSelector } from "react-redux";
import { RootState } from "../../../state/store";
import { UserStory } from "../types";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Divider,
    Chip,
    Card,
    CardContent,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    LinearProgress,
    useMediaQuery,
    useTheme,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    Alert,
    Stack,
    TextareaAutosize,
    OutlinedInput,
    InputAdornment,
    Switch,
    FormControlLabel,
    Tooltip,
    Grid,
    Backdrop,
    CircularProgress,
    Fade,
    Skeleton
} from "@mui/material";
import { LinearProgressIndicator, CircularProgressIndicator } from "./ProgressIndicator";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Checkbox } from "@mui/material";
import {
    Close as CloseIcon,
    AutoFixHigh as AutoFixHighIcon,
    Lightbulb as LightbulbIcon,
    Assignment as AssignmentIcon,
    Speed as SpeedIcon,
    Security as SecurityIcon,
    Analytics as AnalyticsIcon,
    CheckCircle as CheckCircleIcon,
    Info as InfoIcon,
    AutoAwesome as MagicWandIcon,
    CloudUpload as CloudUploadIcon,
    Search as SearchIcon,
    Description as DocumentIcon,
    Image as ImageIcon,
    TableChart as SpreadsheetIcon,
    PictureAsPdf as PdfIcon,
    Code as CodeIcon,
    PlayArrow as VideoIcon,
    AudioFile as AudioIcon,
    Archive as ArchiveIcon,
    InsertDriveFile as FileIcon,
    FolderOpen as BrowseIcon,
    SmartToy as AIIcon,
    BugReport as JiraIcon,
    DesignServices as FigmaIcon,
    Launch as LaunchIcon
} from "@mui/icons-material";

interface AddNewUserStoryModalProps {
    open: boolean;
    onClose: () => void;
    onUserStoryCreated?: () => void; // Callback to refresh user stories list
    featureId?: number | string;
    featureName?: string;
    projectId?: number;
    userStoryId?: string; // If provided, edit mode is enabled
}

type ModalState = "default" | "processing" | "success";

interface UserStoryFormData {
    ticket_summary: string;
    title: string;
    user_story: string;
    description: string;
}

interface ArtifactSuggestion {
    id: string;
    title: string;
    description: string;
    type: "template" | "pattern" | "integration";
    icon: React.ReactNode;
    confidence: number;
    workProducts: string[];
}

export default function AddNewUserStoryModal({
    open,
    onClose,
    onUserStoryCreated,
    featureId,
    featureName,
    projectId,
    userStoryId
}: AddNewUserStoryModalProps) {
    // Get auth token and current project from Redux store
    const token = useSelector((state: RootState) => state.clientToken.token);
    const currentProjectFromStore = useSelector((state: RootState) => state.projects.currentProject);
    const userId = useSelector((state: RootState) => state.userProfiles.userProfile?.userid);

    // Use projectId from props first, fallback to Redux store
    const actualProjectId = projectId || currentProjectFromStore?.project_id || currentProjectFromStore?.projectid;
    const isEditMode = !!userStoryId;
    const [isLoading, setIsLoading] = useState(false);

    // Workflow tracking state
    const [workflowPhase, setWorkflowPhase] = useState<string>("");
    const [workflowStatus, setWorkflowStatus] = useState<string[]>([]);
    const [comparisonJobId, setComparisonJobId] = useState<string | null>(null);
    const [comparisonResult, setComparisonResult] = useState<any>(null);
    const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Add CSS animations for enhanced interactions
    React.useEffect(() => {
        const style = document.createElement("style");
        style.textContent = `
      @keyframes slideInFromRight {
        0% {
          transform: translateX(100%);
          opacity: 0;
        }
        100% {
          transform: translateX(0);
          opacity: 1;
        }
      }
      
      @keyframes checkmarkPop {
        0% { transform: scale(1); }
        50% { transform: scale(1.2); }
        100% { transform: scale(1); }
      }
      
      @keyframes chipSlideIn {
        0% {
          transform: translateX(-20px);
          opacity: 0;
        }
        100% {
          transform: translateX(0);
          opacity: 1;
        }
      }
      
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
    `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [formData, setFormData] = useState<UserStoryFormData>({
        ticket_summary: "",
        title: "",
        user_story: "",
        description: ""
    });

    // Modal state management
    const [modalState, setModalState] = useState<ModalState>("default");
    const [creationProgress, setCreationProgress] = useState(0);
    const [createdFeatureId, setCreatedFeatureId] = useState<string | null>(null);

    const handleInputChange = (field: keyof UserStoryFormData, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Load existing user story data in edit mode
    useEffect(() => {
        if (open && isEditMode && userStoryId && token) {
            setIsLoading(true);
            getUserStory(userStoryId, token)
                .then((story) => {
                    setFormData({
                        ticket_summary: story.ticket_summary || "",
                        title: story.title,
                        user_story: story.user_story,
                        description: story.description || ""
                    });
                })
                .catch((error) => {
                    console.error("Failed to load user story:", error);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else if (open && !isEditMode) {
            // Reset form in create mode
            setFormData({
                ticket_summary: "",
                title: "",
                user_story: "",
                description: ""
            });
        }
    }, [open, isEditMode, userStoryId, token]);

    // Cleanup polling timeout on unmount
    useEffect(() => {
        return () => {
            if (pollTimeoutRef.current) {
                clearTimeout(pollTimeoutRef.current);
                pollTimeoutRef.current = null;
            }
        };
    }, []);

    const resetForm = () => {
        setFormData({
            ticket_summary: "",
            title: "",
            user_story: "",
            description: ""
        });
        setModalState("default");
        setCreationProgress(0);
        setIsLoading(false);
        setWorkflowPhase("");
        setWorkflowStatus([]);
        setComparisonJobId(null);
        setComparisonResult(null);
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
        }
    };

    const handleViewInStudio = () => {
        // Close the modal and reset form
        resetForm();
        onClose();
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSubmit = async () => {
        console.log("=== handleSubmit called ===");
        console.log("Token:", token ? "present" : "missing");
        console.log("actualProjectId:", actualProjectId);
        console.log("formData:", formData);

        if (!token) {
            console.error("Authentication token is missing.");
            alert("Authentication error. Please refresh the page and log in again.");
            return;
        }

        if (!actualProjectId) {
            console.error("Project ID is missing:", {
                projectIdProp: projectId,
                currentProjectFromStore: currentProjectFromStore?.project_id || currentProjectFromStore?.projectid
            });
            alert("Please select a product before creating a user story.");
            return;
        }

        if (!userId) {
            console.error("User ID is missing - cannot proceed with workflow");
            alert("User information not available. Please refresh the page.");
            return;
        }

        // Start processing state
        setModalState("processing");
        setCreationProgress(0);
        setWorkflowStatus([]);

        try {
            console.log("Starting user story creation...");
            setWorkflowStatus(prev => [...prev, "Creating user story..."]);

            // Progress animation (15%)
            setCreationProgress(15);

            const payload = {
                project_id: typeof actualProjectId === "string" ? parseInt(actualProjectId, 10) : actualProjectId,
                title: formData.title,
                user_story: formData.user_story,
                ticket_summary: formData.ticket_summary || undefined,
                description: formData.description || undefined,
                feature_id: featureId ? Number(featureId) : undefined,
                status: "draft"
            };

            console.log("Payload:", payload);

            let result;
            if (isEditMode && userStoryId) {
                console.log("Updating user story:", userStoryId);
                result = await updateUserStory(userStoryId, payload, token);
                setWorkflowStatus(prev => [...prev, "✓ User story updated"]);
            } else {
                console.log("Creating new user story");
                result = await createUserStory(payload, token);
                setWorkflowStatus(prev => [...prev, "✓ User story created"]);
            }

            console.log("User story created/updated:", result);

            // Progress animation (30%)
            setCreationProgress(30);

            // Step 2: Start quick-compare to analyze specification changes
            console.log("🔵 Starting quick-compare analysis...");
            setWorkflowStatus(prev => [...prev, "Analyzing specification changes..."]);

            const quickCompareRequest = {
                projectid: typeof actualProjectId === "string" ? parseInt(actualProjectId, 10) : actualProjectId,
                userid: userId,
                original_answers: [], // Empty for new user story
                new_answers: [],
                is_new_user_story: true,
                feature_id: featureId ? Number(featureId) : undefined,
                user_story_id: result.id
            };

            const quickCompareResponse = await startQuickCompare(quickCompareRequest, token);
            const jobId = quickCompareResponse.job_id;
            setComparisonJobId(jobId);
            console.log("Quick compare job started:", jobId);
            setWorkflowStatus(prev => [...prev, `Job ${jobId} started`]);

            // Progress animation (40%)
            setCreationProgress(40);

            // Step 3: Poll for quick-compare status
            await pollQuickCompareStatus(jobId, result);

        } catch (error) {
            console.error(`Failed to ${isEditMode ? "update" : "create"} user story:`, error);
            setWorkflowStatus(prev => [...prev, `❌ Error: ${error instanceof Error ? error.message : "Unknown error"}`]);
            alert(`Failed to ${isEditMode ? "update" : "create"} user story: ${error instanceof Error ? error.message : "Unknown error"}`);
            setModalState("default");
            setCreationProgress(0);
        }
    };

    const pollQuickCompareStatus = async (jobId: string, userStoryResult: any): Promise<void> => {
        try {
            if (!token || !actualProjectId || !userId) {
                setWorkflowStatus(prev => [...prev, "⚠ Missing required data"]);
                setModalState("default");
                return;
            }

            const status = await getCompareStatus(jobId, token);
            console.log("Quick compare status:", status);

            // Update progress based on status
            if (status.step && status.total_steps) {
                const progress = 40 + Math.floor((status.step / status.total_steps) * 30); // 40-70%
                setCreationProgress(progress);
            }

            // Update workflow status with current step
            if (status.message) {
                setWorkflowPhase(status.message);
                setWorkflowStatus(prev => {
                    const filtered = prev.filter(s => !s.startsWith("Step "));
                    return [...filtered, `Step ${status.step}/${status.total_steps}: ${status.message}`];
                });
            }

            if (status.status === "COMPLETED") {
                console.log("✓ Quick comparison complete");
                setWorkflowStatus(prev => [...prev, "✓ Quick comparison complete"]);
                setCreationProgress(70);
                setComparisonResult(status.comparison_result);

                // Step 4: Apply specification changes
                await applyChanges(userStoryResult, status.comparison_result);

            } else if (status.status === "FAILED") {
                console.error("Quick compare failed:", status.error);
                setWorkflowStatus(prev => [...prev, `⚠ Analysis failed: ${status.error || "Unknown error"}`]);

                // Still mark work products for rebuild even if quick-compare failed
                await markWorkProductsForRebuild(userStoryResult);

            } else {
                // Still processing, poll again
                pollTimeoutRef.current = setTimeout(() => pollQuickCompareStatus(jobId, userStoryResult), 2000);
            }
        } catch (error) {
            console.error("Error polling quick compare status:", error);
            setWorkflowStatus(prev => [...prev, `⚠ Status check failed: ${error instanceof Error ? error.message : "Unknown"}`]);

            // Still try to mark work products for rebuild
            await markWorkProductsForRebuild(userStoryResult);
        }
    };

    const applyChanges = async (userStoryResult: any, comparisonResult: any): Promise<void> => {
        try {
            if (!token || !actualProjectId || !userId) return;

            console.log("🔵 Applying specification changes...");
            setWorkflowStatus(prev => [...prev, "Applying changes to work products..."]);
            setCreationProgress(75);

            const applyRequest = {
                projectid: typeof actualProjectId === "string" ? parseInt(actualProjectId, 10) : actualProjectId,
                userid: userId,
                action: "UPDATE_PRODUCT" as const,
                comparison_result: comparisonResult
            };

            await applySpecificationChanges(applyRequest, token);
            console.log("✓ Specification changes applied");
            setWorkflowStatus(prev => [...prev, "✓ Changes applied to work products"]);
            setCreationProgress(85);

            // Step 5: Mark work products for rebuild
            await markWorkProductsForRebuild(userStoryResult);

        } catch (error) {
            console.error("Error applying changes:", error);
            setWorkflowStatus(prev => [...prev, `⚠ Failed to apply changes: ${error instanceof Error ? error.message : "Unknown"}`]);

            // Still try to mark work products for rebuild
            await markWorkProductsForRebuild(userStoryResult);
        }
    };

    const markWorkProductsForRebuild = async (userStoryResult: any): Promise<void> => {
        try {
            if (!token || !actualProjectId) return;

            console.log("🔵 Marking work products for rebuild...");
            setWorkflowStatus(prev => [...prev, "Marking work products for rebuild..."]);
            setCreationProgress(90);

            await markWorkProductsNeedingRebuild(
                actualProjectId ? [typeof actualProjectId === "string" ? parseInt(actualProjectId, 10) : actualProjectId] : null,
                featureId ? [typeof featureId === "string" ? parseInt(featureId, 10) : featureId] : null,
                userStoryResult.id ? [userStoryResult.id] : null,
                null,
                true, // Use file_update path for scoped marking without source warehouse
                token
            );
            console.log("✅ Successfully marked work products for rebuild");
            setWorkflowStatus(prev => [...prev, "✓ Work products marked for rebuild"]);
            setCreationProgress(100);

            // Move to success state
            setTimeout(() => {
                setModalState("success");
                // Call the callback to refresh the list
                if (onUserStoryCreated) {
                    onUserStoryCreated();
                }
            }, 500);

        } catch (error) {
            console.error("❌ Error marking work products for rebuild:", error);
            setWorkflowStatus(prev => [...prev, `⚠ Failed to mark for rebuild: ${error instanceof Error ? error.message : "Unknown"}`]);

            // Still show success since user story was created
            setCreationProgress(100);
            setTimeout(() => {
                setModalState("success");
                if (onUserStoryCreated) {
                    onUserStoryCreated();
                }
            }, 500);
        }
    };

    const isFormValid = formData.title.trim() && formData.user_story.trim();
    const hasErrors = !formData.title.trim() || !formData.user_story.trim();

    // Skeleton loader component for processing state
    const renderFeatureDetailsSkeleton = () => (
        <Box sx={{ p: 3 }}>
            <Skeleton variant="text" sx={{ fontSize: "1.5rem", mb: 3, width: "60%" }} />

            <Stack spacing={3}>
                {/* Title skeleton */}
                <Box>
                    <Skeleton variant="text" sx={{ fontSize: "1rem", mb: 1, width: "20%" }} />
                    <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                    <Skeleton variant="text" sx={{ fontSize: "0.75rem", mt: 0.5, width: "30%" }} />
                </Box>

                {/* Description skeleton */}
                <Box>
                    <Skeleton variant="text" sx={{ fontSize: "1rem", mb: 1, width: "25%" }} />
                    <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
                    <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1, mt: 1, width: "40%" }} />
                </Box>

                {/* Goals skeleton */}
                <Box>
                    <Skeleton variant="text" sx={{ fontSize: "1rem", mb: 1, width: "15%" }} />
                    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                        <Skeleton variant="rectangular" height={32} width={120} sx={{ borderRadius: 2 }} />
                        <Skeleton variant="rectangular" height={32} width={160} sx={{ borderRadius: 2 }} />
                    </Box>
                    <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                </Box>

                {/* Success Metrics skeleton */}
                <Box>
                    <Skeleton variant="text" sx={{ fontSize: "1rem", mb: 1, width: "30%" }} />
                    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                        <Skeleton variant="rectangular" height={32} width={100} sx={{ borderRadius: 2 }} />
                        <Skeleton variant="rectangular" height={32} width={140} sx={{ borderRadius: 2 }} />
                        <Skeleton variant="rectangular" height={32} width={80} sx={{ borderRadius: 2 }} />
                    </Box>
                    <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                </Box>
            </Stack>
        </Box>
    );

    // Success state component
    const renderSuccessState = () => (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                p: 4,
                textAlign: "center"
            }}
        >
            <CheckCircleIcon
                sx={{
                    fontSize: 80,
                    color: "success.main",
                    mb: 3
                }}
            />
            <Typography
                variant="h4"
                sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    mb: 2
                }}
            >
                User Story {isEditMode ? "Updated" : "Created"} Successfully!
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    color: "text.secondary",
                    mb: 4,
                    maxWidth: 400
                }}
            >
                Your user story "{formData.title}" has been {isEditMode ? "updated" : "created"} and is ready for development. You can now view it in Feature Studio to start building.
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
                <Button variant="outlined" onClick={handleClose} sx={{ textTransform: "none" }}>
                    Close
                </Button>
                <Button variant="contained" onClick={handleViewInStudio} startIcon={<LaunchIcon />} sx={{ textTransform: "none" }}>
                    View in Studio
                </Button>
            </Box>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            PaperProps={{
                sx: {
                    width: isMobile ? "60vw" : "80vw",
                    height: isMobile ? "100vh" : "80vh",
                    maxHeight: isMobile ? "100vh" : "80vh",
                    m: isMobile ? 0 : 2,
                    borderRadius: isMobile ? 0 : 2
                }
            }}
        >
            {/* Modal Header - Page.Header Layout Style */}
            <DialogTitle
                sx={{
                    px: 3,
                    py: 2,
                    bgcolor: "#F1F5FE", // Consistent Page.Header background
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    minHeight: 80 // Match Page.Header height
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                    {/* Modal Title */}
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 600,
                            color: "text.primary",
                            fontSize: "1.5rem",
                            whiteSpace: "nowrap"
                        }}
                    >
                        {isEditMode ? "Edit User Story" : "Add New User Story"}
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} sx={{ color: "text.secondary" }} aria-label="Close Add New User Story Modal">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* Modal Content */}
            <DialogContent
                sx={{
                    p: 0,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    position: "relative"
                }}
            >
                {modalState === "success" ? (
                    // Success state - full width
                    renderSuccessState()
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: isMobile ? "column" : "row",
                            height: "100%",
                            gap: isMobile ? 2 : 0,
                            transition: "all 0.3s ease-in-out"
                        }}
                    >
                        {/* Left Column - Feature Details Form (40% width) */}
                        <Box
                            sx={{
                                width: isMobile ? "100%" : "100%",
                                borderRight: isMobile ? "none" : 1,
                                borderColor: "divider",
                                overflow: "auto",
                                transition: "all 0.3s ease-in-out",
                                transform: isMobile ? "translateX(0)" : "translateX(0)",
                                opacity: 1
                            }}
                        >
                            {modalState === "processing" || isLoading ? (
                                renderFeatureDetailsSkeleton()
                            ) : (
                                <Box sx={{ p: 3 }}>
                                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }} component="h2" role="heading" aria-level={2}>
                                        User Story Details
                                    </Typography>

                                    <Stack spacing={3}>
                                        {/* Title */}
                                        <Box>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    Title <span style={{ color: "red" }}>*</span>
                                                </Typography>
                                                <MagicWandIcon sx={{ fontSize: 16, color: "primary.main" }} />
                                            </Box>
                                            <TextField
                                                fullWidth
                                                placeholder="Enter a descriptive user story title..."
                                                value={formData.title}
                                                onChange={e => handleInputChange("title", e.target.value)}
                                                required
                                                error={hasErrors && !formData.title.trim()}
                                                helperText={hasErrors && !formData.title.trim() ? "Title is required" : "Keep it concise (max 255 characters)"}
                                                aria-label="User story title input field"
                                                inputProps={{ maxLength: 255 }}
                                                InputProps={{
                                                    endAdornment: formData.title && (
                                                        <InputAdornment position="end">
                                                            <MagicWandIcon sx={{ fontSize: 16, color: "primary.main", opacity: 0.7 }} />
                                                        </InputAdornment>
                                                    ),
                                                    sx: {
                                                        "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                                                            borderColor: "error.main",
                                                            borderWidth: 2
                                                        }
                                                    }
                                                }}
                                                FormHelperTextProps={{
                                                    sx: {
                                                        color: hasErrors && !formData.title.trim() ? "error.main" : "text.secondary"
                                                    }
                                                }}
                                            />
                                        </Box>

                                        {/* User Story */}
                                        <Box>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    User Story <span style={{ color: "red" }}>*</span>
                                                </Typography>
                                                <MagicWandIcon sx={{ fontSize: 16, color: "primary.main" }} />
                                            </Box>
                                            <TextareaAutosize
                                                minRows={4}
                                                maxRows={8}
                                                placeholder="As a [user type], I want [goal] so that [benefit]..."
                                                value={formData.user_story}
                                                onChange={e => handleInputChange("user_story", e.target.value)}
                                                aria-label="User story text area"
                                                style={{
                                                    width: "100%",
                                                    minHeight: "100px",
                                                    padding: "12px",
                                                    border: hasErrors && !formData.user_story.trim() ? "2px solid #d32f2f" : "1px solid #ddd",
                                                    borderRadius: "8px",
                                                    fontFamily: "Roboto, sans-serif",
                                                    fontSize: "14px",
                                                    resize: "vertical",
                                                    outline: "none",
                                                    borderColor: formData.user_story ? "#103699" : hasErrors && !formData.user_story.trim() ? "#d32f2f" : "#ddd",
                                                    transition: "all 0.3s ease"
                                                }}
                                            />
                                            {hasErrors && !formData.user_story.trim() && (
                                                <Typography variant="caption" sx={{ color: "error.main", mt: 0.5, display: "block" }}>
                                                    User story is required
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Ticket Summary */}
                                        <Box>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    Ticket Summary
                                                </Typography>
                                                <MagicWandIcon sx={{ fontSize: 16, color: "primary.main" }} />
                                            </Box>
                                            <TextareaAutosize
                                                minRows={3}
                                                maxRows={6}
                                                placeholder="Provide a summary of the ticket or work item (max 3000 characters)..."
                                                value={formData.ticket_summary}
                                                onChange={e => handleInputChange("ticket_summary", e.target.value)}
                                                aria-label="Ticket summary text area"
                                                style={{
                                                    width: "100%",
                                                    minHeight: "80px",
                                                    padding: "12px",
                                                    border: "1px solid #ddd",
                                                    borderRadius: "8px",
                                                    fontFamily: "Roboto, sans-serif",
                                                    fontSize: "14px",
                                                    resize: "vertical",
                                                    outline: "none",
                                                    borderColor: formData.ticket_summary ? "#103699" : "#ddd",
                                                    transition: "all 0.3s ease"
                                                }}
                                            />
                                        </Box>

                                        {/* Description */}
                                        <Box>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    Description
                                                </Typography>
                                                <MagicWandIcon sx={{ fontSize: 16, color: "primary.main" }} />
                                            </Box>
                                            <TextareaAutosize
                                                minRows={3}
                                                maxRows={6}
                                                placeholder="Describe additional details, acceptance criteria, or notes..."
                                                value={formData.description}
                                                onChange={e => handleInputChange("description", e.target.value)}
                                                aria-label="Description text area"
                                                style={{
                                                    width: "100%",
                                                    minHeight: "80px",
                                                    padding: "12px",
                                                    border: "1px solid #ddd",
                                                    borderRadius: "8px",
                                                    fontFamily: "Roboto, sans-serif",
                                                    fontSize: "14px",
                                                    resize: "vertical",
                                                    outline: "none",
                                                    borderColor: formData.description ? "#103699" : "#ddd",
                                                    transition: "all 0.3s ease"
                                                }}
                                            />
                                        </Box>
                                    </Stack>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>

            {/* Fixed Action Bar */}
            {modalState !== "success" && (
                <DialogActions
                    sx={{
                        px: 3,
                        py: 2,
                        borderTop: 1,
                        borderColor: "divider",
                        bgcolor: "background.paper",
                        position: "sticky",
                        bottom: 0,
                        zIndex: 1,
                        display: "flex",
                        justifyContent: modalState === "processing" ? "center" : "space-between",
                        alignItems: "center"
                    }}
                >
                    {modalState === "processing" ? (
                        // Processing state - show enhanced progress indicator with workflow status
                        <Box sx={{ width: "100%", px: 2 }}>
                            <LinearProgressIndicator
                                variant="determinate"
                                value={creationProgress}
                                showStatusText={true}
                                statusText={workflowPhase || "Processing user story..."}
                                currentStep={Math.floor((creationProgress / 100) * 6) + 1}
                                totalSteps={6}
                                step={
                                    creationProgress < 15
                                        ? "Creating user story..."
                                        : creationProgress < 40
                                        ? "Starting specification analysis..."
                                        : creationProgress < 70
                                        ? "Analyzing changes..."
                                        : creationProgress < 85
                                        ? "Applying changes to work products..."
                                        : creationProgress < 100
                                        ? "Marking work products for rebuild..."
                                        : "Finalizing..."
                                }
                                placement="local"
                                status="loading"
                                aria-label="Creating user story"
                            />
                            {/* Show workflow status messages */}
                            {workflowStatus.length > 0 && (
                                <Box sx={{ mt: 2, maxHeight: 150, overflow: "auto" }}>
                                    {workflowStatus.slice(-5).map((status, index) => (
                                        <Typography
                                            key={index}
                                            variant="caption"
                                            sx={{
                                                display: "block",
                                                color: status.startsWith("✓")
                                                    ? "success.main"
                                                    : status.startsWith("⚠") || status.startsWith("❌")
                                                    ? "warning.main"
                                                    : "text.secondary",
                                                mb: 0.5,
                                                fontFamily: "monospace"
                                            }}
                                        >
                                            {status}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    ) : (
                        // Default state - show buttons
                        <>
                            <Button onClick={handleClose} variant="outlined" sx={{ textTransform: "none" }} aria-label="Cancel user story creation">
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                variant="contained"
                                disabled={!isFormValid || isLoading}
                                sx={{ textTransform: "none" }}
                                aria-label={isEditMode ? "Update user story" : "Create new user story"}
                                startIcon={isFormValid ? <InfoIcon sx={{ fontSize: 16, color: "inherit" }} /> : undefined}
                            >
                                {isEditMode ? "Update User Story" : "Create User Story"}
                            </Button>
                        </>
                    )}
                </DialogActions>
            )}
        </Dialog>
    );
}
